import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { View } from "@common/types/views/views";
import { useCallback, useMemo, useState } from "react";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { HistoryRow } from "../views/historyViewConfig";
import { HistoryDetailsTableMeta } from "./HistoryUpdatedTransactionDetails";
import styles from "./HistoryDetails.module.scss";
import {
    createCategoryFirstDefaultHistoryCreatedTransactionDetailsView,
    createGlFirstDefaultHistoryCreatedTransactionDetailsView,
    withGlFirstLedgerColumn,
} from "./transactions/views/defaultHistoryTransactionDetailsView";
import {
    createCategoryFirstHistoryCreatedTransactionDetailsViewConfig,
    createGlFirstHistoryCreatedTransactionDetailsViewConfig,
    HistoryCreatedTransactionDetailsRow,
} from "./transactions/views/historyCreatedTransactionDetailsViewConfig";

interface HistoryCreatedTransactionDetailsProps {
    data: HistoryCreatedTransactionDetailsRow[];
    footer: React.ReactNode;
    historyRow: HistoryRow;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
}

function parseOpenableTxnId(
    row: HistoryCreatedTransactionDetailsRow,
): number | undefined {
    if (!row.canOpenTransaction) {
        return undefined;
    }
    const n = Number(row.id);
    if (!Number.isFinite(n)) {
        return undefined;
    }
    return n;
}

export const HistoryCreatedTransactionDetails: React.FC<
    HistoryCreatedTransactionDetailsProps
> = ({
    data,
    footer,
    historyRow,
    rowSelection: controlledRowSelection,
    onRowSelectionChange: controlledOnRowSelectionChange,
}) => {
    const { enabled: isGlFirst } = useIsGlFirst();

    const config = useMemo(
        () =>
            isGlFirst
                ? createGlFirstHistoryCreatedTransactionDetailsViewConfig()
                : createCategoryFirstHistoryCreatedTransactionDetailsViewConfig(),
        [isGlFirst],
    );

    const [view, setView] = useState<View>(() =>
        isGlFirst
            ? createGlFirstDefaultHistoryCreatedTransactionDetailsView()
            : createCategoryFirstDefaultHistoryCreatedTransactionDetailsView(),
    );
    const normalizedView = useMemo(
        () => withGlFirstLedgerColumn(view, isGlFirst),
        [view, isGlFirst],
    );
    const [uncontrolledRowSelection, setUncontrolledRowSelection] =
        useState<RowSelectionState>({});

    const rowSelection = controlledRowSelection ?? uncontrolledRowSelection;
    const onRowSelectionChange =
        controlledOnRowSelectionChange ?? setUncontrolledRowSelection;

    const openTransactionViewDrawer = useGlobalTransactionViewDrawer();

    const handleRowAction = useCallback(
        (row: HistoryCreatedTransactionDetailsRow) => {
            const txnId = parseOpenableTxnId(row);
            if (txnId === undefined) {
                return;
            }
            openTransactionViewDrawer({
                entityIds: historyRow.entityIds,
                transactionIds: [txnId],
            });
        },
        [historyRow, openTransactionViewDrawer],
    );

    const tableMeta: HistoryDetailsTableMeta = useMemo(
        () => ({
            callbacks: {
                historyRow,
                onCreatedRowAction: handleRowAction,
            },
        }),
        [historyRow, handleRowAction],
    );

    const handleRowClick = useCallback(
        (row?: HistoryCreatedTransactionDetailsRow) => {
            if (!row) {
                return;
            }
            handleRowAction(row);
        },
        [handleRowAction],
    );

    const enableRowSelection = useCallback(
        (row: HistoryCreatedTransactionDetailsRow) =>
            parseOpenableTxnId(row) !== undefined,
        [],
    );

    return (
        <ViewTable
            data={data}
            view={normalizedView}
            config={config}
            onViewChange={setView}
            onRowClick={handleRowClick}
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
            enableRowSelection={enableRowSelection}
            emptyView={() => <EmptyState header="No details available" />}
            footer={footer}
            tableMeta={tableMeta}
            className={styles.historyDetailsView}
            fixedTopOffsetPx
        />
    );
};
